import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-light-svg-icons";
import Select from "react-select";
import Button from "../../../../buttons/Button";

const CalendarControls = ({
  viewMode,
  setViewMode,
  currentDate,
  navigateDate,
  goToToday,
  filterTable,
  setFilterTable,
  filterStatus,
  setFilterStatus,
  tables = [],
  tKey = "dashboard.bookings.calendar.",
}) => {
  const { t } = useTranslation();

  const getDateRangeText = () => {
    const options = { month: "long", day: "numeric", year: "numeric" };

    switch (viewMode) {
      case "daily":
        return currentDate.toLocaleDateString("fr-FR", options);
      case "weekly": {
        const startOfWeek = new Date(currentDate);
        const dayOfWeek = startOfWeek.getDay();
        // Start week on Monday
        startOfWeek.setDate(
          startOfWeek.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1)
        );
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return `${startOfWeek.toLocaleDateString("fr-FR", {
          day: "numeric",
          month: "short",
        })} - ${endOfWeek.toLocaleDateString("fr-FR", options)}`;
      }
      case "monthly":
        return currentDate.toLocaleDateString("fr-FR", {
          month: "long",
          year: "numeric",
        });
      default:
        return "";
    }
  };

  // Build options for the table filter
  const tableOptions = useMemo(() => {
    const allTables = tables.flatMap((room) => room.tables || []);
    return allTables.map((table) => ({
      value: table.tableId,
      label: table.room ? `${table.name} - ${table.room.name}` : table.name,
    }));
  }, [tables]);

  // Build options for the status filter
  const statusOptions = useMemo(
    () => [
      { value: "pending", label: t("dashboard.bookings.status.pending") },
      { value: "confirmed", label: t("dashboard.bookings.status.confirmed") },
      { value: "declined", label: t("dashboard.bookings.status.declined") },
      { value: "canceled", label: t("dashboard.bookings.status.canceled") },
    ],
    [t]
  );

  // Find the currently selected option objects
  const selectedTableOption =
    tableOptions.find((o) => String(o.value) === String(filterTable)) || null;
  const selectedStatusOption =
    statusOptions.find((o) => o.value === filterStatus) || null;

  return (
    <div className="bookings-controls">
      <div className="bookings-controls__row">
        {/* Navigation */}
        <div className="bookings-controls__nav">
          <button
            className="bookings-controls__nav-btn"
            onClick={() => navigateDate("prev")}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <Button variant="info" size="xs" onClick={goToToday}>
            {t(`${tKey}today`)}
          </Button>
          <button
            className="bookings-controls__nav-btn"
            onClick={() => navigateDate("next")}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
          <span className="bookings-controls__date-label">
            {getDateRangeText()}
          </span>
        </div>

        {/* View Mode Buttons */}
        <div className="bookings-controls__view-modes">
          {["daily", "weekly", "monthly"].map((mode) => (
            <Button
              key={mode}
              variant={viewMode === mode ? "success" : "white"}
              size="xs"
              onClick={() => setViewMode(mode)}
            >
              {t(`${tKey}${mode}`)}
            </Button>
          ))}
        </div>

        {/* Filters */}
        <div className="bookings-controls__filters search-module">
          <Select
            className="react-custom-select dashboard-select"
            classNamePrefix="react-select"
            placeholder={t(`${tKey}allTables`)}
            options={tableOptions}
            value={selectedTableOption}
            isClearable
            isSearchable={false}
            onChange={(option) => setFilterTable(option?.value || null)}
          />
          <Select
            className="react-custom-select dashboard-select"
            classNamePrefix="react-select"
            placeholder={t(`${tKey}allStatuses`)}
            options={statusOptions}
            value={selectedStatusOption}
            isClearable
            isSearchable={false}
            onChange={(option) => setFilterStatus(option?.value || null)}
          />
        </div>
      </div>
    </div>
  );
};

export default CalendarControls;
